import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Modal } from '@/components/ui/Modal';

interface EditGuestModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    onDelete?: (guestId: string, guestName: string) => Promise<void>;
    guest: any | null;
}

export const EditGuestModal: React.FC<EditGuestModalProps> = ({ isOpen, onClose, onSuccess, onDelete, guest }) => {
    const [fullName, setFullName] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [events, setEvents] = useState<string[]>([]);
    const [plusOneAllowed, setPlusOneAllowed] = useState(false);
    const [plusChildren, setPlusChildren] = useState(false);
    const [country, setCountry] = useState('IE');
    
    // RSVP related
    const [rsvpIe, setRsvpIe] = useState('');
    const [rsvpEs, setRsvpEs] = useState('');
    const [rsvpPb, setRsvpPb] = useState('');
    
    // Bus related
    const [busIe, setBusIe] = useState('');
    const [busEsTo, setBusEsTo] = useState('');
    const [busEsReturn, setBusEsReturn] = useState('');

    const [dietaryGuest, setDietaryGuest] = useState('');
    const [dietaryPlusOne, setDietaryPlusOne] = useState('');
    const [dietaryChildren, setDietaryChildren] = useState<string[]>([]);
    const [notes, setNotes] = useState('');
    const [plusOneName, setPlusOneName] = useState('');
    const [childrenCount, setChildrenCount] = useState<number | ''>('')
    const [childrenNames, setChildrenNames] = useState<string[]>([]);
    const [weddingParty, setWeddingParty] = useState(false);
    const [tableNumber, setTableNumber] = useState('');
    const [accommodation, setAccommodation] = useState('');
    const [accommodationCheckIn, setAccommodationCheckIn] = useState('');
    const [accommodationCheckOut, setAccommodationCheckOut] = useState('');
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (guest) {
            setFullName(guest.fullName || '');
            setInviteCode(guest.inviteCode || '');
            setEvents(guest.events || []);
            setPlusOneAllowed(guest.plusOneAllowed || false);
            setPlusChildren(guest.plusChildren || false);
            setCountry(guest.country || 'IE');
            
            // RSVP
            setRsvpIe(guest.status?.ie || '');
            setRsvpEs(guest.status?.es || '');
            setRsvpPb(guest.status?.pb || '');
            
            // Bus
            setBusIe(guest.bus?.ie || '');
            setBusEsTo(guest.bus?.esTo || '');
            setBusEsReturn(guest.bus?.esReturn || '');
            
            // Handle dietary fallback (string or object)
            if (typeof guest.dietary === 'string') {
                setDietaryGuest(guest.dietary);
                setDietaryPlusOne('');
                setDietaryChildren([]);
            } else if (guest.dietary) {
                setDietaryGuest(guest.dietary.guest || '');
                setDietaryPlusOne(guest.dietary.plusOne || '');
                setDietaryChildren(guest.dietary.children || []);
            } else {
                setDietaryGuest('');
                setDietaryPlusOne('');
                setDietaryChildren([]);
            }
            
            setNotes(guest.notes || '');
            setPlusOneName(guest.plusOneName || '');
            setChildrenCount(guest.children !== undefined ? guest.children : '');
            setChildrenNames(guest.childrenNames || []);
            setWeddingParty(guest.weddingParty || false);
            setTableNumber(guest.tableNumber || '');
            setAccommodation(guest.accommodation || '');
            setAccommodationCheckIn(guest.accommodationCheckIn || '');
            setAccommodationCheckOut(guest.accommodationCheckOut || '');
        }
    }, [guest]);

    useEffect(() => {
        if (typeof childrenCount === 'number' && childrenCount >= 0) {
            setChildrenNames(prev => {
                if (prev.length < childrenCount) {
                    return [...prev, ...Array(childrenCount - prev.length).fill('')];
                }
                return prev.slice(0, childrenCount);
            });
            setDietaryChildren(prev => {
                if (prev.length < childrenCount) {
                    return [...prev, ...Array(childrenCount - prev.length).fill('')];
                }
                return prev.slice(0, childrenCount);
            });
        }
    }, [childrenCount]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!guest) return;
        
        setLoading(true);
        setError('');

        try {
            const guestRef = doc(db, 'guests', guest.id);
            
            const updatedData: any = {
                fullName,
                events,
                plusOneAllowed,
                plusChildren,
                country,
                weddingParty,
                tableNumber,
                accommodation,
                accommodationCheckIn,
                accommodationCheckOut,
                updatedAt: new Date()
            };
            
            // Only update RSVP info if it was explicitly populated in the form
            // Or if existing, write the edited values
            const newStatus = { ...guest.status };
            if (rsvpIe) newStatus.ie = rsvpIe;
            if (rsvpEs) newStatus.es = rsvpEs;
            if (rsvpPb) newStatus.pb = rsvpPb;
            
            if (Object.keys(newStatus).length > 0) {
                updatedData.status = newStatus;
            }
            
            const newBus = { ...guest.bus };
            if (busIe !== '') newBus.ie = busIe;
            if (busEsTo !== '') newBus.esTo = busEsTo;
            if (busEsReturn !== '') newBus.esReturn = busEsReturn;
            
            if (Object.keys(newBus).length > 0) {
                updatedData.bus = newBus;
            }
            
            updatedData.dietary = {
                guest: dietaryGuest,
                plusOne: plusOneAllowed ? dietaryPlusOne : '',
                children: plusChildren ? dietaryChildren : []
            };
            updatedData.notes = notes;
            
            if (plusOneAllowed) {
                updatedData.plusOneName = plusOneName;
            }
            
            if (plusChildren && childrenCount !== '') {
                updatedData.children = Number(childrenCount);
                updatedData.childrenNames = childrenNames;
            }

            await updateDoc(guestRef, updatedData);
            
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to update guest');
        } finally {
            setLoading(false);
        }
    };

    const toggleEvent = (code: string) => {
        setEvents(prev =>
            prev.includes(code)
                ? prev.filter(c => c !== code)
                : [...prev, code]
        );
    };

    if (!guest) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Edit Guest">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 font-sans text-secondary max-h-[80vh] overflow-y-auto pr-2 pb-6">
                {error && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg border border-red-100 text-base">
                        {error}
                    </div>
                )}

                <div className="space-y-4 border-b border-gray-100 pb-4">
                    <h4 className="font-bold text-lg text-primary">Basic Info</h4>
                    
                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                            Full Name
                        </label>
                        <input
                            type="text"
                            required
                            className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                            value={fullName}
                            onChange={e => setFullName(e.target.value)}
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                            Invite Code (Read-Only)
                        </label>
                        <input
                            type="text"
                            disabled
                            className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg outline-none font-mono text-gray-500"
                            value={inviteCode}
                        />
                    </div>

                    <div className="space-y-2">
                        <span className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Events</span>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                                    checked={events.includes('IE')}
                                    onChange={() => toggleEvent('IE')}
                                />
                                <span className="text-base group-hover:text-primary transition-colors">Ireland (IE)</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                                    checked={events.includes('ES')}
                                    onChange={() => toggleEvent('ES')}
                                />
                                <span className="text-base group-hover:text-primary transition-colors">Spain (ES)</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                                    checked={events.includes('PB')}
                                    onChange={() => toggleEvent('PB')}
                                />
                                <span className="text-base group-hover:text-primary transition-colors">Pre-Boda (PB)</span>
                            </label>
                        </div>
                    </div>

                    <div className="space-y-1 mt-4">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                            Table Number <span className="font-normal normal-case opacity-60">(ES)</span>
                        </label>
                        <input
                            type="text"
                            className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                            value={tableNumber}
                            onChange={e => setTableNumber(e.target.value)}
                            placeholder="e.g. Mesa 3, Head Table"
                        />
                    </div>

                    <div className="space-y-1 mt-4">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                            Accommodation <span className="font-normal normal-case opacity-60">(ES)</span>
                        </label>
                        <input
                            type="text"
                            className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                            value={accommodation}
                            onChange={e => setAccommodation(e.target.value)}
                            placeholder="e.g. Hotel Los Llanos, Airbnb..."
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="space-y-1">
                            <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                                Check-in
                            </label>
                            <input
                                type="date"
                                className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                value={accommodationCheckIn}
                                onChange={e => setAccommodationCheckIn(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                                Check-out
                            </label>
                            <input
                                type="date"
                                className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                value={accommodationCheckOut}
                                onChange={e => setAccommodationCheckOut(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="space-y-2 mt-4">
                        <span className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Country</span>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="radio"
                                    name="country"
                                    className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                                    checked={country === 'IE'}
                                    onChange={() => setCountry('IE')}
                                />
                                <span className="text-base group-hover:text-primary transition-colors">Ireland (IE)</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="radio"
                                    name="country"
                                    className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                                    checked={country === 'ES'}
                                    onChange={() => setCountry('ES')}
                                />
                                <span className="text-base group-hover:text-primary transition-colors">Spain (ES)</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                    type="radio"
                                    name="country"
                                    className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                                    checked={country === 'UK'}
                                    onChange={() => setCountry('UK')}
                                />
                                <span className="text-base group-hover:text-primary transition-colors">UK (UK)</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="space-y-4 border-b border-gray-100 pb-4">
                    <h4 className="font-bold text-lg text-primary">Guest Allowed Details</h4>
                    
                    <div className="flex gap-6 flex-wrap">
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="checkbox"
                                className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                                checked={plusOneAllowed}
                                onChange={e => setPlusOneAllowed(e.target.checked)}
                            />
                            <span className="text-base font-medium group-hover:text-primary transition-colors">Plus One Allowed</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="checkbox"
                                className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                                checked={plusChildren}
                                onChange={e => setPlusChildren(e.target.checked)}
                            />
                            <span className="text-base font-medium group-hover:text-primary transition-colors">Children Allowed</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                                type="checkbox"
                                className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300"
                                checked={weddingParty}
                                onChange={e => setWeddingParty(e.target.checked)}
                            />
                            <span className="text-base font-medium group-hover:text-primary transition-colors">Wedding Party Member</span>
                        </label>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {plusOneAllowed && (
                            <div className="space-y-1">
                                <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                                    Plus One Name
                                </label>
                                <input
                                    type="text"
                                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                    value={plusOneName}
                                    onChange={e => setPlusOneName(e.target.value)}
                                    placeholder="Leave blank if unknown"
                                />
                            </div>
                        )}
                        {plusChildren && (
                            <>
                            <div className="space-y-1">
                                <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                                    Number of Children
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                    value={childrenCount}
                                    onChange={e => setChildrenCount(e.target.value ? Number(e.target.value) : '')}
                                    placeholder="0"
                                />
                            </div>
                            {childrenNames.length > 0 && (
                                <div className="col-span-2 space-y-2 mt-2">
                                    <span className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Children Names</span>
                                    {childrenNames.map((name, idx) => (
                                        <input
                                            key={idx}
                                            type="text"
                                            className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                            value={name}
                                            onChange={e => {
                                                const updated = [...childrenNames];
                                                updated[idx] = e.target.value;
                                                setChildrenNames(updated);
                                            }}
                                            placeholder={`Child ${idx + 1} name`}
                                        />
                                    ))}
                                </div>
                            )}
                            </>
                        )}
                    </div>
                </div>

                <div className="space-y-4">
                    <h4 className="font-bold text-lg text-primary">RSVP Status</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-1">
                            <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">IE RSVP</label>
                            <select 
                                className="w-full p-2.5 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                value={rsvpIe}
                                onChange={e => setRsvpIe(e.target.value)}
                            >
                                <option value="">No Response</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">ES RSVP</label>
                            <select 
                                className="w-full p-2.5 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                value={rsvpEs}
                                onChange={e => setRsvpEs(e.target.value)}
                            >
                                <option value="">No Response</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">PB RSVP</label>
                            <select 
                                className="w-full p-2.5 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                value={rsvpPb}
                                onChange={e => setRsvpPb(e.target.value)}
                            >
                                <option value="">No Response</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>
                    </div>

                    <h4 className="font-bold text-lg text-primary mt-6">Bus Status</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-1">
                            <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">IE Bus</label>
                            <select 
                                className="w-full p-2.5 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                value={busIe}
                                onChange={e => setBusIe(e.target.value)}
                            >
                                <option value="">No Response</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">ES Bus To Venue</label>
                            <select 
                                className="w-full p-2.5 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                value={busEsTo}
                                onChange={e => setBusEsTo(e.target.value)}
                            >
                                <option value="">No Response</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">ES Bus Return</label>
                            <select 
                                className="w-full p-2.5 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                value={busEsReturn}
                                onChange={e => setBusEsReturn(e.target.value)}
                            >
                                <option value="">No Response</option>
                                <option value="early">Early (1:30am)</option>
                                <option value="late">Late (5:00am)</option>
                                <option value="none">No Bus</option>
                            </select>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="space-y-1">
                            <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                                Primary Guest Dietary
                            </label>
                            <input
                                type="text"
                                className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                value={dietaryGuest}
                                onChange={e => setDietaryGuest(e.target.value)}
                                placeholder="None"
                            />
                        </div>

                        {plusOneAllowed && (
                            <div className="space-y-1">
                                <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                                    Plus One Dietary ({plusOneName || 'Guest'})
                                </label>
                                <input
                                    type="text"
                                    className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                    value={dietaryPlusOne}
                                    onChange={e => setDietaryPlusOne(e.target.value)}
                                    placeholder="None"
                                />
                            </div>
                        )}

                        {plusChildren && childrenCount !== '' && Number(childrenCount) > 0 && (
                            <div className="space-y-2">
                                <span className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Children Dietary Requirements</span>
                                {Array.from({ length: Number(childrenCount) }).map((_, idx) => (
                                    <div key={idx} className="space-y-1">
                                        <label className="block text-xs font-semibold text-gray-500">
                                            {childrenNames[idx] || `Child ${idx + 1}`}
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all animate-fade-in"
                                            value={dietaryChildren[idx] || ''}
                                            onChange={e => {
                                                const updated = [...dietaryChildren];
                                                updated[idx] = e.target.value;
                                                setDietaryChildren(updated);
                                            }}
                                            placeholder="None"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">
                            Notes
                        </label>
                        <textarea
                            className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none h-24"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Any extra info..."
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100 sticky bottom-0 bg-white">
                    {onDelete && (
                        <button
                            type="button"
                            className="flex items-center gap-1.5 text-sm font-medium text-red-400 hover:text-red-600 transition-colors mr-auto"
                            onClick={async () => {
                                if (guest) {
                                    await onDelete(guest.id, guest.fullName);
                                    onClose();
                                }
                            }}
                            disabled={loading}
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Delete Guest
                        </button>
                    )}
                    <button type="button" className="btn btn-ghost px-6 py-2 rounded-full text-base font-bold border border-gray-200 hover:bg-gray-50 transition-colors" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary px-6 py-2 rounded-full text-base font-bold bg-primary text-white hover:bg-primary-dark transition-all shadow-md active:scale-95" disabled={loading}>
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};
